import { useCallback, useEffect, useRef, useState } from "react";

import { api } from "./client";
import { useRealtimeScope } from "../realtime/useRealtimeScope";

// A tailed log line (specifications/services.md §Logs). `seq` is a per-entity
// monotonic cursor used for backfill paging + stream de-dup.
//
// Both collections have logs, and both are read the same way — only the
// endpoint and the event name differ, so `collection` selects between them.
export type ServiceLogLine = {
  readonly at: string;
  readonly level: "error" | "info" | "warn";
  readonly message: string;
  readonly seq: number;
};

// Bounded client-side ring buffer of recent (live-window) lines — older lines
// drop from here as new ones arrive. Lines pulled in from an archived chunk
// via `loadOlder` live in a separate, unbounded bucket (see below) so paging
// into deep history doesn't get evicted by the next live append.
const MAX_LINES = 300;

// Backfill-then-subscribe (specifications/services.md §Log pane): fetch a page
// of recent lines on mount, then append `service.log.appended` lines whose seq
// exceeds the last rendered one, dropping duplicates from the overlap. Intended
// to be mounted with `key={serviceId}` so switching services resets cleanly.
export type LogCollection = "processes" | "services";

// One archived chunk's metadata, as listed by `GET .../log-archives`
// (specifications/services.md §Logs: deep history). `id` addresses
// `GET .../log-archives/:id/content` — `key` is an object-storage path and
// can't double as a URL segment.
export type LogArchiveSummary = {
  readonly bytes: number | null;
  readonly from: string;
  readonly id: number;
  readonly key: string;
  readonly to: string;
};

export type UseServiceLogs = {
  // More archived history exists above what's currently loaded.
  readonly hasOlder: boolean;
  readonly lines: ReadonlyArray<ServiceLogLine>;
  readonly loadingOlder: boolean;
  readonly loadOlder: () => void;
};

export const useServiceLogs = (
  taskId: string,
  serviceId: string,
  collection: LogCollection = "services",
): UseServiceLogs => {
  const [lines, setLines] = useState<ReadonlyArray<ServiceLogLine>>([]);
  // Deep history pulled in from archived chunks, oldest first, kept separate
  // from the live-window ring buffer above so it survives further live
  // appends trimming `lines`.
  const [olderLines, setOlderLines] = useState<ReadonlyArray<ServiceLogLine>>(
    [],
  );
  const lastSeqRef = useRef(0);
  // Archived chunks not yet merged into `olderLines`, newest first — the
  // archive-listing endpoint returns them oldest-first (chunk order matches
  // flush order), so this is that list reversed. `fetchNextArchiveChunk`
  // always takes the head, walking deep history from most to least recent.
  const pendingArchivesRef = useRef<ReadonlyArray<LogArchiveSummary>>([]);
  const loadingOlderRef = useRef(false);
  const [hasOlder, setHasOlder] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);

  // Fetches the next (i.e. most recent unloaded) archived chunk whole and
  // prepends its lines (services.md §Logs: deep history — a chunk is fetched
  // whole, not paged by line). A chunk that fails to load is left at the head
  // of the queue so retrying (another scroll, or the effect below) doesn't
  // silently skip a gap in the history.
  const fetchNextArchiveChunk = useCallback((): void => {
    const archive = pendingArchivesRef.current[0];
    if (!archive || loadingOlderRef.current) {
      return;
    }
    loadingOlderRef.current = true;
    setLoadingOlder(true);
    void api
      .get<{ lines?: ReadonlyArray<ServiceLogLine> }>(
        `/${collection}/${serviceId}/log-archives/${String(archive.id)}/content`,
      )
      .then((res) => {
        pendingArchivesRef.current = pendingArchivesRef.current.slice(1);
        setHasOlder(pendingArchivesRef.current.length > 0);
        setOlderLines((prev) => [...(res.lines ?? []), ...prev]);
      })
      .catch(() => undefined)
      .finally(() => {
        loadingOlderRef.current = false;
        setLoadingOlder(false);
      });
  }, [collection, serviceId]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      let backfillEmpty = true;
      try {
        const res = await api.get<{ lines?: ReadonlyArray<ServiceLogLine> }>(
          `/${collection}/${serviceId}/logs?after_seq=0&limit=${String(MAX_LINES)}`,
        );
        if (cancelled) {
          return;
        }
        const backfill = res.lines ?? [];
        if (backfill.length > 0) {
          backfillEmpty = false;
          lastSeqRef.current = backfill[backfill.length - 1]!.seq;
          setLines(backfill.slice(-MAX_LINES));
        }
      } catch {
        // The live backfill failing shouldn't block deep history below.
      }
      if (cancelled) {
        return;
      }
      // List archived chunks so scrolling past the live window has deep
      // history to reach for (services.md §Logs: deep history). Metadata
      // only — chunk bodies are fetched lazily, one at a time.
      try {
        const res = await api.get<{
          archives?: ReadonlyArray<LogArchiveSummary>;
        }>(`/${collection}/${serviceId}/log-archives`);
        if (cancelled) {
          return;
        }
        const archives = [...(res.archives ?? [])].reverse();
        pendingArchivesRef.current = archives;
        setHasOlder(archives.length > 0);
        // Archive-only (services.md §Log-pane states): the live window came
        // back empty — sandbox gone, or everything already flushed past
        // retention — but archived chunks exist. Surface the most recent one
        // immediately instead of a pane that reads as "no logs at all".
        if (backfillEmpty && archives.length > 0) {
          fetchNextArchiveChunk();
        }
      } catch {
        // No archives to page into; the live window (if any) still renders.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [serviceId, collection, fetchNextArchiveChunk]);

  useRealtimeScope(`task:${taskId}`, (event) => {
    const expected =
      collection === "processes"
        ? "process.log.appended"
        : "service.log.appended";
    if (event.kind !== expected) {
      return;
    }
    const summary = (event.summary ?? {}) as {
      lines?: ReadonlyArray<ServiceLogLine>;
      process_public_id?: string;
      service_public_id?: string;
    };
    const owner = summary.process_public_id ?? summary.service_public_id;
    if (owner !== serviceId) {
      return;
    }
    const incoming = (summary.lines ?? []).filter(
      (l) => l.seq > lastSeqRef.current,
    );
    if (incoming.length === 0) {
      return;
    }
    lastSeqRef.current = incoming[incoming.length - 1]!.seq;
    setLines((prev) => [...prev, ...incoming].slice(-MAX_LINES));
  });

  return {
    hasOlder,
    lines: olderLines.length > 0 ? [...olderLines, ...lines] : lines,
    loadingOlder,
    loadOlder: fetchNextArchiveChunk,
  };
};
